import { initializeApp, getApp, type FirebaseApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, connectAuthEmulator, type Auth } from 'firebase/auth';
import {
	initializeFirestore,
	connectFirestoreEmulator,
	persistentLocalCache,
	persistentMultipleTabManager,
	memoryLocalCache,
	type Firestore
} from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { browser } from '$app/environment';

const useFirebaseEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';

/**
 * The emulators run under `demo-updraft` (test_locally.sh, printEmulatorEnv.js),
 * not the production id in `.env`. The emulator namespaces data by project id,
 * so the app has to ask for the same one or it sees an empty database.
 */
const EMULATOR_PROJECT_ID = 'demo-updraft';

const firebaseConfig = {
	apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
	authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
	projectId: useFirebaseEmulator ? EMULATOR_PROJECT_ID : import.meta.env.VITE_FIREBASE_PROJECT_ID,
	storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
	messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
	appId: import.meta.env.VITE_FIREBASE_APP_ID
};

/**
 * True when a real Firebase project is configured via .env.
 *
 * A fresh clone (or a fresh Codespace) has no .env, so every one of these is
 * undefined. Firebase does not tolerate that: `getAuth()` throws
 * `auth/invalid-api-key` at *module load*, which takes down anything that
 * transitively imports this file — including Storybook stories that render
 * pure-prop components and never touch the network. Storybook and story tests
 * should work with zero credentials, so when the config is absent we degrade
 * instead of throwing: `app` is initialized with obviously-fake placeholder
 * values (so `getStorage(app)` and friends still have an app to hang off) and
 * `auth`/`db` are left null, which every consumer already handles because they
 * are null during SSR too.
 */
export const isFirebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

const placeholderConfig = {
	apiKey: 'missing-firebase-config',
	authDomain: 'missing-firebase-config.firebaseapp.com',
	projectId: 'missing-firebase-config',
	storageBucket: 'missing-firebase-config.appspot.com',
	messagingSenderId: '000000000000',
	appId: '1:000000000000:web:0000000000000000000000'
};

if (!isFirebaseConfigured && browser && !import.meta.env.STORYBOOK) {
	console.warn(
		'⚠️ Firebase is not configured (no VITE_FIREBASE_API_KEY / VITE_FIREBASE_PROJECT_ID).\n' +
			'   Auth and Firestore are disabled. Copy webapp/.env.example to webapp/.env and fill it in\n' +
			'   to run the real app. Storybook and story tests work without this.'
	);
}

// Get existing app or initialize new one
let app: FirebaseApp;
try {
	app = getApp();
} catch {
	app = initializeApp(isFirebaseConfigured ? firebaseConfig : placeholderConfig);
}

// Export app for use in other modules (e.g., Storage)
export { app };

// Only initialize Firebase services in the browser
export const provider = new GoogleAuthProvider();

const servicesEnabled = browser && isFirebaseConfigured;

export const auth: Auth | null = servicesEnabled ? getAuth(app) : null;

/**
 * Callable functions. Used by client error reporting, which is the one path
 * that must work even when a page is otherwise broken — see
 * `$lib/state/errorReporting.svelte`.
 */
export const functions: Functions | null = servicesEnabled ? getFunctions(app) : null;

// Initialize Firestore with persistent local cache (new API)
//
// persistentLocalCache({}) defaults to a single-tab manager: only the first
// tab opened gets the IndexedDB cache, every other tab falls back to
// memory-only and pays full server reads. Staff routinely have several tabs
// open, so we opt into the multi-tab manager, which synchronizes the same
// on-disk cache (and in-flight writes) across all of them.
export const db: Firestore | null = servicesEnabled
	? initializeFirestore(app, {
			localCache: useFirebaseEmulator
				? memoryLocalCache()
				: persistentLocalCache({ tabManager: persistentMultipleTabManager() })
		})
	: null;

if (browser && auth && db) {
	// Connect to emulators if in development mode
	if (useFirebaseEmulator) {
		try {
			connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
			connectFirestoreEmulator(db, 'localhost', 8087);
			if (functions) connectFunctionsEmulator(functions, 'localhost', 5001);
			console.log('🔧 Connected to Firebase emulators');
			console.log('🔧 Firestore app:', db.app.name, db.app.options);
			console.log('🔧 Firestore type:', db.type);
		} catch (error) {
			console.warn(
				'Emulator connection failed:',
				error instanceof Error ? error.message : 'Unknown error'
			);
		}
	}
	console.log('✅ Firestore initialized with persistent local cache');
}
